import { useMemo } from 'react'

import useBreakpoint from 'Utils/useBreakpoint'

import useChartPagination from './useChartPagination'
import isMobileDevice from '../../../utils/isMobileDevice'
import { getChartOptions, getBarColors } from '../chartConfig/chartConfig'
import { DAYS_PER_PAGE } from '../constants/chartConstants'
import { formatDateLabel } from '../utils/formatters'

function usePerformanceChart(data, boostPeriods, metricType) {
  const breakpoint = useBreakpoint()
  const isMobile = breakpoint === 'xs' || isMobileDevice()

  const { allLabels, allValues } = useMemo(() => ({
    allLabels: Object.keys(data),
    allValues: Object.values(data),
  }), [data])

  const {
    currentPage,
    totalPages,
    periodLabel,
    goToPrevious,
    goToNext,
    canGoPrevious,
    canGoNext,
  } = useChartPagination(allLabels.length)

  const { labels, rawLabels, values } = useMemo(() => {
    if (!isMobile) {
      return {
        labels: allLabels.map(formatDateLabel),
        rawLabels: allLabels,
        values: allValues,
      }
    }

    const startIndex = allLabels.length - ((currentPage + 1) * DAYS_PER_PAGE)
    const endIndex = allLabels.length - (currentPage * DAYS_PER_PAGE)
    const slicedLabels = allLabels.slice(Math.max(0, startIndex), endIndex)

    return {
      labels: slicedLabels.map(formatDateLabel),
      rawLabels: slicedLabels,
      values: allValues.slice(Math.max(0, startIndex), endIndex),
    }
  }, [allLabels, allValues, isMobile, currentPage])

  const chartData = useMemo(() => ({
    labels,
    datasets: [{
      data: values,
      backgroundColor: getBarColors(rawLabels, boostPeriods),
      borderWidth: 0,
      barPercentage: isMobile ? 0.9 : 0.8,
    }],
  }), [labels, rawLabels, values, boostPeriods, isMobile])

  const options = useMemo(() => getChartOptions(metricType, isMobile), [metricType, isMobile])

  return {
    isMobile,
    chartData,
    options,
    totalPages,
    currentPage,
    periodLabel,
    goToPrevious,
    goToNext,
    canGoPrevious,
    canGoNext,
  }
}

export default usePerformanceChart
